import React, {useEffect, useState} from "react";
import {get} from "aws-amplify/api";
import CompactSessionReport from "./CompactSessionReport";
import ChartsReport from "./ChartsReport";
import {BackgroundColorContext} from "../../contexts/BackgroundColorContext";
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Input,
  Button,
  InputGroup,
  ModalBody,
  Modal,
  ModalHeader
} from "reactstrap";
import {useUser} from "../../contexts/AuthContext";
import awsconfig from "../../configs/awsconfig.json";
import ReactGA from "react-ga4";
import {GoogleAnalyticsEventCategories} from "../../constants";
import {notify} from "../../utils/toast";
import {RingLoader} from "react-spinners";
import {checkAnalysisExists} from "../../services/advancedAnalysisService";
import {AnalysisStatus} from "../../types/advancedAnalysis";
import {dataSeriesLength} from "../../utils/dataAlignment";

function filterActivities(activities: any[]): any[] {
  return activities.filter((activity: any) => activity.worldId !== 0);
}

function getActivitiesOptions(activities: any[]) {
  return activities.map((activity: any, index: number) => ({
    value: `${index}`,
    label: `${activity.world} - ${activity.humanizedDuration}`
  }));
}

function getLongestActivityIndex(activities: any[]): number | undefined {
  // show the longest activity by sorting
  const activitiesSortedByLength: any | undefined = activities
    .filter((x: any) => {
      return x.xaxis && dataSeriesLength(x.xaxis) > 0;
    })
    .sort((a: any, b: any) => dataSeriesLength(b.xaxis) - dataSeriesLength(a.xaxis));

  if (activitiesSortedByLength.length === 0) {
    return undefined;
  }

  return activitiesSortedByLength[0].index;
}

// Notification function is now imported from utils/toast

const LogReport = (props: any) => {
  const [loading, setLoading] = useState(true);
  const {user} = useUser();
  const [link, setLink] = useState("");
  const [modalShare, setModalShare] = useState(false);
  const allActivities: any[] = props.activities || [];
  const filteredActivities: any[] = filterActivities(allActivities);

  const indexedActivities: any[] = filteredActivities.map((activity: any, index: number) => {
    activity.index = index;
    return activity;
  });

  const options: any = getActivitiesOptions(filteredActivities);
  const initialSelectedOption: number | undefined = getLongestActivityIndex(indexedActivities);
  const [selectedOption, setSelectedOption] = useState(initialSelectedOption);

  // Shared analysis status for all activities (analysis is per-log, not per-activity)
  const [analysisStatus, setAnalysisStatus] = useState<AnalysisStatus>("checking");

  const toggleModalShare = () => {
    setModalShare(!modalShare);
  };

  async function copyToClipBoard(link: string) {
    try {
      await navigator.clipboard.writeText(link);
      notify("Link copied to clipboard");
    } catch {
      //console.log(err);
    }
  }

  async function getShareLink() {
    const userId: string = user.identityId as string;
    const link: string = `${awsconfig.Environment.hostname}/public/${props.logId}`;

    ReactGA.event({
      category: GoogleAnalyticsEventCategories.REPORT,
      action: "get share link",
      label: `${link}`
    });

    try {
      const params: any = {
        headers: {
          Authorization: user.jwtToken
        }
      };
      await get({
        apiName: "app",
        path: `/share/${userId}/${props.logId}`,
        options: params
      });
      setLink(link);
      await copyToClipBoard(link);
    } catch {
      //console.log(`error getting share link: ${err}`);
      return undefined;
    }
  }

  // Check if analysis exists on mount
  useEffect(() => {
    const checkExistence = async () => {
      // For demo pages, advanced analysis is always available locally
      if (props.isDemo) {
        setAnalysisStatus("exists");
        return;
      }

      if (!user.identityId || !props.logId || !user.jwtToken) {
        setAnalysisStatus("not_exists");
        return;
      }

      try {
        const exists = await checkAnalysisExists(user.identityId, props.logId, user.jwtToken);
        setAnalysisStatus(exists ? "exists" : "not_exists");
      } catch (error) {
        setAnalysisStatus("not_exists");
      }
    };

    checkExistence();
  }, [user.identityId, props.logId, props.jwtToken, props.isDemo]);

  // useEffect to delay render until all props are set
  useEffect(() => {
    setLoading(false);
  }, []);

  if (loading === true) {
    return (
      <BackgroundColorContext.Consumer>
        {() => (
          <div className="iconWrapper">
            <RingLoader color="#4682b4" size={80} loading={true} />
          </div>
        )}
      </BackgroundColorContext.Consumer>
    );
  }

  // Check if authentication is required
  if (loading === false && props.isAuthenticationRequired) {
    return (
      <BackgroundColorContext.Consumer>
        {({color}) => (
          <Card>
            <CardHeader data-color={color} className="no-body">
              <CardTitle>Authentication Required</CardTitle>
            </CardHeader>
            <CardBody>
              <p>You must be signed in to view this log report.</p>
              <p>
                <a href="/auth/signin" className="btn btn-primary">
                  Sign In
                </a>
              </p>
            </CardBody>
          </Card>
        )}
      </BackgroundColorContext.Consumer>
    );
  }

  if (loading === false && (props.activities === undefined || props.activities.length === 0)) {
    return (
      <BackgroundColorContext.Consumer>
        {({color}) => (
          <Card>
            <CardHeader data-color={color} className="no-body">
              <CardTitle>Error</CardTitle>
            </CardHeader>
            <CardBody>
              <p>There was a problem parsing the log file.</p>
            </CardBody>
          </Card>
        )}
      </BackgroundColorContext.Consumer>
    );
  }

  if (loading === false) {
    return (
      <BackgroundColorContext.Consumer>
        {({color}) => (
          <>
            <CompactSessionReport
              {...props.session}
              {...props.system}
              activitiesCount={options.length}
              isShareable={props.isShareable}
              toggleModalShare={toggleModalShare}
            />
            {/* Pre-render all activities but only show the selected one */}
            {filteredActivities.map((activity, index) => (
              <div key={`activity-charts-${index}`} style={{display: selectedOption === index ? "block" : "none"}}>
                <ChartsReport
                  {...activity}
                  system={props.system}
                  session={props.session}
                  id={props.id}
                  activityOptions={options}
                  selectedActivity={selectedOption}
                  onActivityChange={setSelectedOption}
                  userId={user.identityId}
                  logId={props.logId}
                  jwtToken={user.jwtToken}
                  analysisStatus={analysisStatus}
                  onAnalysisStatusChange={setAnalysisStatus}
                  isPublic={props.isPublic}
                  isDemo={props.isDemo}
                  timezoneOffsetMinutes={user.settings.timezoneOffsetMinutes}
                />
              </div>
            ))}
            <Modal modalClassName="modal-share" isOpen={modalShare} toggle={toggleModalShare}>
              <ModalHeader>
                <button aria-label="Close" className="close" onClick={toggleModalShare}>
                  <i className="fa fa-times" />
                </button>
              </ModalHeader>
              <ModalBody>
                <Card>
                  <CardHeader data-color={color}>
                    <CardTitle tag="h3">Share</CardTitle>
                  </CardHeader>
                  <CardBody>
                    <p>
                      By clicking 'Get Link' you agree to make this page public. <br />
                      (Your raw log file and personal info is always protected).
                    </p>

                    <InputGroup>
                      <Input
                        type="text"
                        readOnly={true}
                        name="shareLink"
                        id="shareLink"
                        placeholder="https://"
                        value={link}
                      />

                      <Button
                        color="primary"
                        data-color={color}
                        onClick={() => {
                          copyToClipBoard(link);
                        }}
                      >
                        <i className="fa fa-copy" />
                      </Button>
                    </InputGroup>
                    <Button color="primary" data-color={color} onClick={getShareLink}>
                      Get Link
                    </Button>
                    <Button color="primary" data-color={color} onClick={toggleModalShare}>
                      Close
                    </Button>
                    <p className="subtext">Share links expire automatically after 2 years.</p>
                  </CardBody>
                </Card>
              </ModalBody>
            </Modal>
          </>
        )}
      </BackgroundColorContext.Consumer>
    );
  }

  return <></>;
};

export default LogReport;
